import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import FootballDictionarySelect from "./FootballDictionarySelect";
import AuthImage from "./AuthImage";
import {FootballKit, FootballRefOptions, FOOTBALL_IMAGE_BASE} from "../service/footballTypes";
import Dialog from "./Dialog";
import {PlusIcon, XIcon} from "./icons";

interface Props {
    editItem?: FootballKit | null;
    refs: FootballRefOptions;
    onClose: () => void;
    onSaved: () => void;
}

/** Create/edit form for a football kit (комплект): jersey + optional shorts. */
const AddFootballItemModal: React.FC<Props> = ({editItem, refs, onClose, onSaved}) => {
    // classification
    const [clubId, setClubId] = useState('');
    const [clubName, setClubName] = useState('');
    const [seasonId, setSeasonId] = useState('');
    const [seasonName, setSeasonName] = useState('');
    const [kitTypeId, setKitTypeId] = useState('');
    const [notes, setNotes] = useState('');

    // jersey part
    const [jerseyVersionId, setJerseyVersionId] = useState('');
    const [jerseySizeId, setJerseySizeId] = useState('');
    const [jerseyNumber, setJerseyNumber] = useState('');
    const [playerName, setPlayerName] = useState('');
    const [sleevesTypeId, setSleevesTypeId] = useState('');
    const [patchId, setPatchId] = useState('');
    const [patchName, setPatchName] = useState('');
    const [jerseyPhotos, setJerseyPhotos] = useState<string[]>([]);

    // shorts part (optional) — filled independently of the jersey
    const [hasShorts, setHasShorts] = useState(false);
    const [shortsVersionId, setShortsVersionId] = useState('');
    const [shortsSizeId, setShortsSizeId] = useState('');
    const [shortsNumber, setShortsNumber] = useState('');
    const [shortsPhotos, setShortsPhotos] = useState<string[]>([]);

    const [uploading, setUploading] = useState<'jersey' | 'shorts' | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!editItem) return;
        setClubId(editItem.club?.id ?? '');
        setClubName(editItem.club?.name ?? '');
        setSeasonId(editItem.season?.id ?? '');
        setSeasonName(editItem.season?.name ?? '');
        setKitTypeId(editItem.kitType?.id ?? '');
        setNotes(editItem.notes ?? '');

        setJerseyVersionId(editItem.jersey?.version?.id ?? '');
        setJerseySizeId(editItem.jersey?.size?.id ?? '');
        setJerseyNumber(editItem.jersey?.number != null ? String(editItem.jersey.number) : '');
        setPlayerName(editItem.jersey?.playerName ?? '');
        setSleevesTypeId(editItem.jersey?.sleevesType?.id ?? '');
        setPatchId(editItem.jersey?.patch?.id ?? '');
        setPatchName(editItem.jersey?.patch?.name ?? '');
        setJerseyPhotos(editItem.jersey?.photoUrls ?? []);

        if (editItem.shorts) {
            setHasShorts(true);
            setShortsVersionId(editItem.shorts.version?.id ?? '');
            setShortsSizeId(editItem.shorts.size?.id ?? '');
            setShortsNumber(editItem.shorts.number != null ? String(editItem.shorts.number) : '');
            setShortsPhotos(editItem.shorts.photoUrls ?? []);
        }
    }, [editItem]);

    const upload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>, target: 'jersey' | 'shorts') => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(target);
        try {
            const api = await createAxiosInstance();
            const form = new FormData();
            form.append('file', file);
            const res = await api.post<{ key: string }>('/football/images', form, {
                headers: {'Content-Type': 'multipart/form-data'}
            });
            if (target === 'jersey') {
                setJerseyPhotos(prev => [...prev, res.data.key]);
            } else {
                setShortsPhotos(prev => [...prev, res.data.key]);
            }
        } catch (err) {
            console.error('Failed to upload image:', err);
        } finally {
            setUploading(null);
            e.target.value = '';
        }
    }, []);

    const canSave = !!clubId && !!seasonId && !!kitTypeId && !saving;

    const handleSave = useCallback(async () => {
        if (!clubId || !seasonId || !kitTypeId) return;
        setSaving(true);
        try {
            const api = await createAxiosInstance();
            const body = {
                clubId,
                seasonId,
                kitTypeId,
                notes: notes.trim() || null,
                jersey: {
                    versionId: jerseyVersionId || null,
                    sizeId: jerseySizeId || null,
                    number: jerseyNumber.trim() ? parseInt(jerseyNumber, 10) : null,
                    playerName: playerName.trim() || null,
                    sleevesTypeId: sleevesTypeId || null,
                    patchId: patchId || null,
                    photoUrls: jerseyPhotos,
                },
                shorts: hasShorts ? {
                    versionId: shortsVersionId || null,
                    sizeId: shortsSizeId || null,
                    number: shortsNumber.trim() ? parseInt(shortsNumber, 10) : null,
                    photoUrls: shortsPhotos,
                } : null,
            };
            if (editItem) {
                await api.put(`/football/kits/${editItem.id}`, body);
            } else {
                await api.post('/football/kits', body);
            }
            onSaved();
        } catch (err) {
            console.error('Failed to save football kit:', err);
        } finally {
            setSaving(false);
        }
    }, [clubId, seasonId, kitTypeId, notes, jerseyVersionId, jerseySizeId, jerseyNumber, playerName,
        sleevesTypeId, patchId, jerseyPhotos, hasShorts, shortsVersionId, shortsSizeId, shortsNumber,
        shortsPhotos, editItem, onSaved]);

    const title = `${editItem ? 'Редактировать' : 'Добавить'} комплект`;

    return (
        <Dialog
            open
            onClose={onClose}
            title={title}
            busy={saving}
            data-testid="add-football-item-modal"
            panelClassName="bg-white dark:bg-ink-800 rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto outline-none"
            headerClassName="flex items-center justify-between gap-3 mb-4"
            bodyClassName=""
        >
                <div className="space-y-4">
                    {/* --- classification --- */}
                    <Field label="Клуб *">
                        <FootballDictionarySelect endpoint="clubs" value={clubId} valueName={clubName}
                            onChange={(id, name) => { setClubId(id); setClubName(name); }} placeholder="Например: Chelsea"/>
                    </Field>
                    <Field label="Сезон *">
                        <FootballDictionarySelect endpoint="seasons" value={seasonId} valueName={seasonName}
                            onChange={(id, name) => { setSeasonId(id); setSeasonName(name); }} placeholder="Например: 2024/25"/>
                    </Field>
                    <Field label="Тип *">
                        <select className="input-field w-full" value={kitTypeId} onChange={e => setKitTypeId(e.target.value)}>
                            <option value="">—</option>
                            {refs.kitTypes.map(o => <option key={o.id} value={o.id}>{o.nameRu}</option>)}
                        </select>
                    </Field>

                    {/* --- jersey part (version/size filled independently from shorts) --- */}
                    <div className="pt-2 border-t border-gray-100 dark:border-white/10">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Футболка</div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Версия">
                                <select className="input-field w-full" value={jerseyVersionId} onChange={e => setJerseyVersionId(e.target.value)}>
                                    <option value="">—</option>
                                    {refs.kitVersions.map(o => <option key={o.id} value={o.id}>{o.nameRu}</option>)}
                                </select>
                            </Field>
                            <Field label="Размер">
                                <select className="input-field w-full" value={jerseySizeId} onChange={e => setJerseySizeId(e.target.value)}>
                                    <option value="">—</option>
                                    {refs.sizes.map(o => <option key={o.id} value={o.id}>{o.nameRu}</option>)}
                                </select>
                            </Field>
                        </div>
                        <div className="mt-3 space-y-4">
                            <Field label="Номер">
                                <input type="number" className="input-field w-full" value={jerseyNumber} onChange={e => setJerseyNumber(e.target.value)} placeholder="10"/>
                            </Field>
                            <Field label="Имя игрока">
                                <input className="input-field w-full" value={playerName} onChange={e => setPlayerName(e.target.value)} placeholder="Например: Hazard"/>
                            </Field>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Рукава">
                                    <select className="input-field w-full" value={sleevesTypeId} onChange={e => setSleevesTypeId(e.target.value)}>
                                        <option value="">—</option>
                                        {refs.sleevesTypes.map(o => <option key={o.id} value={o.id}>{o.nameRu}</option>)}
                                    </select>
                                </Field>
                                <Field label="Патч">
                                    <FootballDictionarySelect endpoint="patches" value={patchId} valueName={patchName}
                                        onChange={(id, name) => { setPatchId(id); setPatchName(name); }} placeholder="Патч"/>
                                </Field>
                            </div>
                            <Field label="Фото футболки">
                                <PhotoGrid photos={jerseyPhotos} uploading={uploading === 'jersey'}
                                    onUpload={e => upload(e, 'jersey')}
                                    onRemove={key => setJerseyPhotos(prev => prev.filter(k => k !== key))}/>
                            </Field>
                        </div>
                    </div>

                    {/* --- shorts part (optional) --- */}
                    <div className="pt-2 border-t border-gray-100 dark:border-white/10">
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                            <input type="checkbox" checked={hasShorts} onChange={e => setHasShorts(e.target.checked)}/>
                            Шорты
                        </label>
                        {hasShorts && (
                            <div className="mt-3 space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Версия">
                                        <select className="input-field w-full" value={shortsVersionId} onChange={e => setShortsVersionId(e.target.value)}>
                                            <option value="">—</option>
                                            {refs.kitVersions.map(o => <option key={o.id} value={o.id}>{o.nameRu}</option>)}
                                        </select>
                                    </Field>
                                    <Field label="Размер">
                                        <select className="input-field w-full" value={shortsSizeId} onChange={e => setShortsSizeId(e.target.value)}>
                                            <option value="">—</option>
                                            {refs.sizes.map(o => <option key={o.id} value={o.id}>{o.nameRu}</option>)}
                                        </select>
                                    </Field>
                                </div>
                                <Field label="Номер">
                                    <input type="number" className="input-field w-full" value={shortsNumber} onChange={e => setShortsNumber(e.target.value)} placeholder="10"/>
                                </Field>
                                <Field label="Фото шорт">
                                    <PhotoGrid photos={shortsPhotos} uploading={uploading === 'shorts'}
                                        onUpload={e => upload(e, 'shorts')}
                                        onRemove={key => setShortsPhotos(prev => prev.filter(k => k !== key))}/>
                                </Field>
                            </div>
                        )}
                    </div>

                    <Field label="Заметки">
                        <input className="input-field w-full" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Необязательно"/>
                    </Field>
                </div>

                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={() => !saving && onClose()} className="btn-secondary">Отмена</button>
                    <button onClick={handleSave} disabled={!canSave} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                        {saving ? 'Сохранение...' : 'Сохранить'}
                    </button>
                </div>
                {(!clubId || !seasonId || !kitTypeId) && (
                    <p className="mt-2 text-xs text-gray-400 text-right">Клуб, сезон и тип обязательны</p>
                )}
        </Dialog>
    );
};

const PhotoGrid: React.FC<{
    photos: string[];
    uploading: boolean;
    onUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onRemove: (key: string) => void;
}> = ({photos, uploading, onUpload, onRemove}) => (
    <div className="flex flex-wrap gap-2">
        {photos.map(key => (
            <div key={key} className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                <AuthImage imageKey={key} alt="" basePath={FOOTBALL_IMAGE_BASE} className="w-full h-full object-cover"/>
                <button type="button" onClick={() => onRemove(key)}
                    className="absolute top-0 right-0 bg-black/60 text-white rounded-bl p-0.5">
                    <XIcon className="w-3 h-3"/>
                </button>
            </div>
        ))}
        <label className="w-16 h-16 flex items-center justify-center rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 cursor-pointer text-gray-400 hover:text-gray-600">
            {uploading ? '...' : <PlusIcon className="w-5 h-5"/>}
            <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading}/>
        </label>
    </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({label, children}) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        {children}
    </div>
);

export default AddFootballItemModal;
